import React, {useContext, useEffect, useState} from "react";
import {injectIntl} from "react-intl";
import Modal from "@amzn/meridian/modal";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Input from "@amzn/meridian/input";
import Select, {SelectOption} from "@amzn/meridian/select";
import Button from "@amzn/meridian/button";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import {DraftContext} from "../../../draftSave/context/DraftSaveContext";
import {CONFIG_TYPE, PICK_ITEM_TYPES, OPERATIONS} from "../../../util/Constants";
import {getLocalizedMessage} from "../../../util/Localization";
import {validateLocationMappingRow} from "../../../util/Validations";

const STATE_KEY = CONFIG_TYPE.RETRIEVER_LOCATION_MAPPING;

const AddRetrieverLocationMappingModal = ({intl, open, setOpen, selectedMac, editRow, pickPointResources}) => {

    const {addInstance, editInstance, getDraftData} = useContext(DraftContext);

    const [destination, setDestination] = useState("");
    const [itemType, setItemType] = useState("");
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [error, setError] = useState("");

    const isEdit = !!editRow;

    useEffect(() => {
        if (open && editRow) {
            setDestination(editRow.destination || "");
            setItemType(editRow.type || "");
            setSelectedLocations(editRow.locations || []);
            setError("");
        } else if (open) {
            resetForm();
        }
    }, [open, editRow]);

    const resetForm = () => {
        setDestination("");
        setItemType("");
        setSelectedLocations([]);
        setError("");
    };

    const onClose = () => {
        setOpen(false);
        resetForm();
    };

    const formatValidationError = (result) => {
        return intl.formatMessage({id: result.id}, result.values || {});
    };

    const onConfirm = () => {
        const allRows = getDraftData({stateKey: STATE_KEY, subKey: selectedMac}) || [];
        const existingRows = allRows.filter(function(row) { return row.operation !== OPERATIONS.DELETE; });
        const validationResult = validateLocationMappingRow(
            destination, itemType, selectedLocations, existingRows, isEdit
        );
        if (validationResult) {
            setError(formatValidationError(validationResult));
            return;
        }

        const instance = {
            destination: destination.trim(),
            type: itemType,
            locations: selectedLocations
        };

        if (isEdit) {
            editInstance({
                stateKey: STATE_KEY,
                subKey: selectedMac,
                instance,
                keyComparator: (a, b) => a.destination === b.destination && a.type === b.type,
                valueComparator: (a, b) =>
                    a.destination === b.destination &&
                    a.type === b.type &&
                    JSON.stringify(a.locations) === JSON.stringify(b.locations)
            });
        } else {
            addInstance({
                stateKey: STATE_KEY,
                subKey: selectedMac,
                instance
            });
        }

        onClose();
    };

    const pickPointLabels = pickPointResources.map(r => r.label);

    return (
        <Modal
            title={isEdit
                ? getLocalizedMessage({intl, stringId: "locationmapping.modal.edit_title"})
                : getLocalizedMessage({intl, stringId: "locationmapping.modal.add_title"})}
            open={open}
            onClose={() => {}}
            scrollContainer="viewport"
            width={600}>
            <Column spacing="400">
                <Column spacing="200">
                    <Text type="b300">
                        {getLocalizedMessage({intl, stringId: "locationmapping.destination"})}
                    </Text>
                    <Input
                        value={destination}
                        onChange={(val) => { setDestination(val); setError(""); }}
                        placeholder={getLocalizedMessage({intl, stringId: "locationmapping.destination.placeholder"})}
                        size="small"
                        disabled={isEdit}
                    />
                </Column>
                <Column spacing="200">
                    <Text type="b300">
                        {getLocalizedMessage({intl, stringId: "locationmapping.type"})}
                    </Text>
                    <Select
                        value={itemType}
                        onChange={(val) => { setItemType(val); setError(""); }}
                        placeholder={getLocalizedMessage({intl, stringId: "locationmapping.type.placeholder"})}
                        size="small"
                        disabled={isEdit}>
                        {PICK_ITEM_TYPES.map(type => (
                            <SelectOption key={type} value={type} label={type}/>
                        ))}
                    </Select>
                </Column>
                <Column spacing="200">
                    <Text type="b300">
                        {getLocalizedMessage({intl, stringId: "locationmapping.locations"})}
                    </Text>
                    <Select
                        value={selectedLocations}
                        onChange={(val) => { setSelectedLocations(val); setError(""); }}
                        valueLabel={selectedLocations.length > 0 ? () => `${selectedLocations.length} selected (${selectedLocations.join(", ")})` : undefined}
                        placeholder={getLocalizedMessage({intl, stringId: "locationmapping.locations.placeholder"})}
                        size="small"
                        searchable={true}>
                        {pickPointLabels.map(label => (
                            <SelectOption key={label} value={label} label={label}/>
                        ))}
                    </Select>
                </Column>
                {error && <Alert type="error" size="small">{error}</Alert>}
                <Row spacing="300">
                    <Button type="primary" size="small" onClick={onConfirm}>
                        {getLocalizedMessage({intl, stringId: "modal.confirm"})}
                    </Button>
                    <Button type="secondary" size="small" onClick={onClose}>
                        {getLocalizedMessage({intl, stringId: "modal.cancel"})}
                    </Button>
                </Row>
            </Column>
        </Modal>
    );
};

export default injectIntl(AddRetrieverLocationMappingModal);



// WEBPACK FOOTER //
// ./src/components/devicemanagement/autoretriever/modal/AddRetrieverLocationMappingModal.js